<script setup>
import { computed } from "vue";
import { getStatusText, matchStatusToSvg } from "@/services/api/Mappings.js";
import { useCommunicatorStore } from '@/stores/communicator.js';
import { useWorkfilesStore } from '@/stores/workfiles.js';
import { storeToRefs } from "pinia";
import { useRouter } from 'vue-router';
const workfilesStore = useWorkfilesStore();
const communicatorStore = useCommunicatorStore();
const { getGlobalSearchItems: searchList, getShowList: showList } = storeToRefs(communicatorStore);
const router = useRouter();
const props = defineProps({
    focused: Boolean
});
const items = computed(() => {
    return searchList.value;
});
const show = computed(() => {
    return showList.value && props.focused;
});
const emit = defineEmits(['clear']);
function parseDate(timestamp){
    return new Date(parseInt(timestamp) * 1000).toLocaleString([], {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).replace(',', '');
}
function getColor(status){
    return workfilesStore.getStatusColor(status, '0');
}
function goTo(controller,id){
    router.push({ name: controller, params: { id: id } });
    emit('clear');
}
</script>
<template>
    <div class="search-list-dropdown ease-enter" :class="{'show': show}" id="searchList">
        <div class="workfiles" v-if="items && items.workfiles && items.workfiles.length">
            <div class="title">
                {{ $t('field.reparations') }}
            </div>
            <div class="workfiles-list">
                <div class="workfile" v-for="workfile in items.workfiles" @click="goTo('workfile', workfile.id)">
                    <div class="status-svg">
                        <div class="status-svg-wrapper" :style="{'background-color': getColor(workfile.status)}">
                            <img v-if="[99,100,101,102,103].includes(workfile.status)" style="margin-left:-6px" v-bind:src="'/assets/workfiles/'+ matchStatusToSvg(workfile.status, workfile.type) +'.svg'" />
                            <img v-else v-bind:src="'/assets/workfiles/'+ matchStatusToSvg(workfile.status, workfile.type) +'.svg'" />
                        </div>
                    </div>
                    <div class="status-primary">{{ $t(getStatusText(workfile.status, 'primary', workfile.type)) }}</div>
                    <div class="status-secondary">{{ $t(getStatusText(workfile.status, 'secondary', workfile.type)) }}</div>
                    <div class="or">{{ workfile.orNumber }}</div>
                    <div class="plate">{{ workfile.vehicle.plate}}</div>
                    <div class="vin">{{ workfile.vehicle.vin }}</div>
                    <div class="modified">{{ $t('field.modified') }} {{ parseDate(workfile.date) }}</div>
                </div> 
            </div>
        </div>
        <div class="vehicles" v-if="items && items.vehicles && items.vehicles.length">
            <div class="title">
                {{ $t('route.vehicles') }}
            </div>
            <div class="vehicles-list">
                <div class="vehicle" v-for="vehicle in items.vehicles" @click="goTo('vehicle', vehicle.id)">
                    <div class="brand-logo">
                        <img :src="vehicle.svgImage"  v-if="vehicle.svgImage && vehicle.svgImage.length"/>
                        <img :src="vehicle.image"  v-else-if="vehicle.image.length"/>
                    </div>
                    <div class="plate">{{ vehicle.plate }}</div>    
                    <div class="vin">{{ vehicle.vin }}</div>
                    <div class="brand-model">
                        <div class="brand">{{ vehicle.brand }}</div>
                        <div class="separator" v-if="vehicle.brand && vehicle.model"> - </div>
                        <div class="model">{{ vehicle.model }}</div>
                    </div>       
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "@/styles/colors.scss";
.search-list-dropdown{
    *{
        font-family: 'CircularStd-Book';
    }
    background-color: white;
    position: absolute;
    padding:1rem;
    padding-left: 3rem;
    z-index:100   ;
    flex-direction: column;
    gap: 2rem;
    box-shadow: 0px 3px 6px #00000029;
    border-radius: 0px 0px 36px 36px;
    width: max-content;
    height: max-content;
    //animation
    visibility: hidden;
    opacity: 0;
    scale: 0;
    -webkit-transform-origin: top left;
    transform-origin: top left;
    transition: all 0.25s ease;
    &.show{
        //animation
        visibility: visible;
        opacity: 1;
        scale: 1;
    }
    .workfiles,
    .vehicles{
        display: flex;
        flex-direction: column;
        gap: .5rem;
        margin-bottom: .5rem;
    }
    .vehicles{
        gap: 0;
    }
    .workfile,
    .vehicle{
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: start;
        gap: 1rem;
    }
    .workfile:hover, .vehicle:hover{
        cursor: pointer;
        background: #EFF3F3;
        border-radius: 45px;
    }
    .workfiles-list{
        display: flex;
        flex-direction: column;
        gap: 9px;
        .workfile{
            .status-primary, 
            .plate{
                font-family: 'CircularStd-Black';
                font-size: 15px;
            }
            .status-svg{
                .status-svg-wrapper{
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    img{
                        max-width: 18px;
                        max-height: 18px;
                        width: auto;
                        height: auto;
                        object-fit: contain;
                    }
                }
                display: flex;
                align-items: center;
                justify-content: start;
                width: 40px;
            }
            .status-secondary{
                font-size: 15px;
            }
            .plate{
                font-family: 'CircularStd-Bold';
                font-size: 15px;
            }
            .vin{
                font-size: 15px;
                color: #000000;
            }
            .modified{
                background: #EFF3F3 0% 0% no-repeat padding-box;
                border-radius: 45px;
                color: #ACB4C1;
                font-size: 14px;
                padding: 0 10px;
            }
    
        }
    }
    .vehicles-list{
        display: flex;
        flex-direction: column;
        margin-bottom: 1rem;
        .vehicle{
            height: 45px;
            .brand-model{
                display: flex;
                flex-direction: row;
                gap: 0.5rem;
            }
            .brand-logo{
                display: flex;
                align-content: center;
                justify-content: start;
                width: 40px;
                img {
                    max-height: 27px;
                    max-width: 37px;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                    @media (max-width:768px) {
                        max-height: 40px;
                        max-width: 40px;
                    }
                }            
            }
            .plate{
                font-family: 'CircularStd-Black';
                font-size: 15px;
            }
            .vin,
            .brand,
            .model{
                font-family: 'CircularStd-Book';
                letter-spacing: 0px;
                font-size: 15px;
            }
            .brand{
                font-family: 'CircularStd-Bold';
            }
        }
    }
    .title{
        font-size: 20px;
        color: #ACB4C1;
        letter-spacing: 0px;
    }
    @media (max-width:576px) {
        top: 20px;
        left: 0px;
        width: 100dvw;
        padding-left: 8px;
        .workfiles{
            margin-top: 10px;
        }
        .workfile{
            display: grid;
            grid-template-columns: 45px 90px 2fr;
            grid-template-rows: auto auto;
            grid-template-areas:
                "svg primary or"
                "svg plate vin";
            row-gap: 0px;
            column-gap: 0px;
            .status-svg{
                grid-area: svg;
            }
            .status-primary{
                grid-area: primary;
            }
            .status-secondary{
                grid-area: secondary;
                display: none;
            }
            .plate{
                grid-area: plate;
            }
            .vin{
                grid-area: vin;
            }
            .modified{
                grid-area: modified;
                display: none;
            }
            .or{
                grid-area: or;
            }
        }
        .workfiles-list{
            gap: 10px;
        }
        .vehicles{
            margin-top: 1rem;
        }
        .vehicle{
            display: grid;
            grid-template-columns: 70px 90px 1fr;
            grid-template-rows: auto auto;
            grid-template-areas:
                "logo brand model"
                "logo plate vin";
            row-gap: 0px;
            column-gap: 0px;
            .brand-logo{
                grid-area: logo;
            }
            .plate{
                grid-area: plate;
            }
            .vin{
                grid-area: vin;
            }
            .brand{
                grid-area: brand;
            }
            .model{
                grid-area: model;
            }
            .separator{
                display: none;
            }
        }
        .vehicles-list{
            margin-top: 10px;
            gap: 5px;
        }
    }
    @media (min-width:577px) and (max-width:1024px) {
        top: 48px;
        left: -20px;
        padding-top: 0;
        width: 100dvw;
        .modified{
            display: none;
        }
    }
    @media (min-width:577px) and (max-width:1024px) and (orientation: portrait) {
        .workfile{
            display: grid;
            grid-template-columns: 45px 90px 1fr 1fr;
            grid-template-rows: auto auto;
            grid-template-areas:
                "svg primary or secondary"
                "svg plate vin vin";
            row-gap: 0px;
            column-gap: 0px;
            .status-svg{
                grid-area: svg;
            }
            .status-primary{
                grid-area: primary;
            }
            .status-secondary{
                grid-area: secondary;
            }
            .plate{
                grid-area: plate;
            }
            .vin{
                grid-area: vin;
            }
            .modified{
                grid-area: modified;
                display: none;
            }
            .or{
                grid-area: or;
            }
        }
    }
    @media (min-width:1025px){
        top: 49px;
        left: -50px;
        width: 1000px;
    }
}


</style>